import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { GET_STAGE } from '../../services/ApolloClient/Queries';
import { config } from '../../services/Constants/AppConstants';
import {
  MenopauseSymptoms,
  OnboardingAnswers,
  OnboardingState,
} from '../../types/onboarding/survey';
import {
  IPathInfo,
  ISurveys,
  SURVEY_TRIGGER,
  SURVEY_TYPES,
  SurveyPersonalizedName,
} from '../../types/surveys';
import { useSurveyData } from '../useSurveyData';

export const useOnboardingState = (onError?: (error: string) => void) => {
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [onboardingData, setOnboardingData] = useState<ISurveys>([]);
  const [onboardingFinished, setOnboardingFinished] = useState<boolean>(false);
  const [onboardingAnswers, setOnboardingAnswers] = useState<OnboardingAnswers>(
    {} as OnboardingAnswers,
  );
  const [showIntro, setShowIntro] = useState<boolean>(true);
  const [responsePathInfo, setResponsePathInfo] = useState<IPathInfo>({} as IPathInfo);
  const [menopauseSymptoms, setMenopauseSymptoms] = useState<MenopauseSymptoms>({});

  const queryParameters = new URLSearchParams(window.location.search);
  const surveyTrigger =
    (queryParameters.get('trigger') as SURVEY_TRIGGER) ?? SURVEY_TRIGGER.ONBOARDING_WEB;

  const surveyName = SurveyPersonalizedName.NEW_ONBOARDING;
  const surveyRequestData = {
    type: SURVEY_TYPES.ONBOARDING_WEB,
    version: config.ONBOARDING_WEB_LATEST_VERSION,
  };

  const [getStageData, { data: stageData, loading: isLoadingStage, refetch: refetchStage }] =
    useLazyQuery(GET_STAGE);

  const handlerGetStageData = useCallback(
    (programDetermined: string) => {
      getStageData({ variables: { stage: programDetermined } });
    },
    [getStageData],
  );

  const {
    surveyData,
    loader: surveyLoader,
    error: surveyError,
  } = useSurveyData(surveyRequestData.type, surveyRequestData.version);

  useEffect(() => {
    if (surveyData) {
      setOnboardingData(surveyData);
      setLoader(false);
    }
    if (surveyError) {
      setError(surveyError);
      onError?.(surveyError);
    }
  }, [surveyData, surveyError, onError, surveyLoader]);

  const showIntroHandler = (): void => setShowIntro(false);

  const state: OnboardingState = {
    loader,
    error,
    onboardingData,
    onboardingFinished,
    onboardingAnswers,
    showIntro,
    responsePathInfo,
    menopauseSymptoms,
  };

  return {
    state,
    setState: {
      setLoader,
      setError,
      setOnboardingData,
      setOnboardingFinished,
      setOnboardingAnswers,
      setShowIntro,
      setResponsePathInfo,
      setMenopauseSymptoms,
    },
    showIntroHandler,
    handlerGetStageData,
    stageData,
    isLoadingStage,
    refetchStage,
    surveyName,
    surveyRequestData,
    surveyTrigger,
  };
};
